import { useMemo } from 'react';
import useLocale from '@/hooks/useLocale';

/**
 * Hook that formats server status for display without triggering API requests.
 * Uses the status data already available from the server object.
 *
 * @param {string} statusValue - The status value from the server object
 * @returns {{ value: string, variant: string } | null}
 */
const useStatusFormatter = (statusValue) => {
  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: vpsOciLocale } = productDetailsLocale;
  const locale = vpsOciLocale.status;

  const status = useMemo(() => {
    if (!statusValue) return null;

    const normalizedStatus = statusValue.toLowerCase();

    switch (normalizedStatus) {
      case 'online':
        return {
          value: locale.online,
          variant: 'success',
        };
      case 'offline':
        return {
          value: locale.offline,
          variant: 'neutral',
        };
      case 'processing':
        return {
          value: locale.processing,
          variant: 'warning',
        };
      case 'rebuild':
        return {
          value: locale.rebuild,
          variant: 'warning',
        };
      case 'password_change':
        return {
          value: locale.passwordChange,
          variant: 'info',
        };
      case 'reboot':
        return {
          value: locale.reboot,
          variant: 'warning',
        };
      case 'docker_manager':
        return {
          value: locale.dockerManager,
          variant: 'info',
        };
      default:
        return {
          value: statusValue,
          variant: 'neutral',
        };
    }
  }, [statusValue, locale]);

  return status;
};

export default useStatusFormatter;
